import { STATUS_STYLES } from "@/components/StatusBadge";
import { daysEarly, daysLeft, targetDate } from "@/lib/ageing";

/*
  One renderer for the Slippage column, shared by the Projects grid, the Tasks
  grid and a project's own Tasks tab — so the same figure never reads three
  different ways.

  THREE CASES, and which one applies is decided by status alone:

    closed / cancelled  ->  the status NAME, in that status's own colour.
                            Both are administrative end states. A number
                            against them answers a question nobody asks: a
                            cancelled task has no delivery to be early or late
                            for, and a closed one was filed rather than
                            shipped. Coloured and dotted to match its status
                            pill, so the two columns agree at a glance.

    live                ->  "Live (+2d)" — the status, then how the delivery
                            actually landed against the target. Fixed, not
                            running: measured against completed_at, the date
                            the work reached a terminal state, so it stops
                            moving the day the work lands.

    everything else     ->  a signed day count. Still in flight, so the clock
                            is still running.

  THE SIGN CONVENTION IS THE READER'S, NOT THE FORMULA'S: positive is
  comfortable, negative is trouble, in every case.

      +5d   five days still to run          (or, live: landed five days early)
       0d   due today                       (or, live: landed on the day)
      -22d  twenty-two days past the date   (or, live: took that much longer)

  Both numbers come from lib/ageing.js, which negates the underlying ageing
  formula for exactly this reason.
*/
const fmtDay = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

const plural = (n) => (Math.abs(n) === 1 ? "day" : "days");
const signed = (n) => `${n > 0 ? "+" : ""}${n}d`;

/* Statuses that report their name instead of a figure. */
const NAME_ONLY = ["closed", "cancelled"];

export default function SlippageCell({ status, eta, revised, completedAt = null }) {
  const style = STATUS_STYLES[status];
  const against = revised ? "revised ETA" : "ETA";
  const on = fmtDay(targetDate(revised, eta));

  /* ---------- closed / cancelled: the name, in its own colour ---------- */
  if (NAME_ONLY.includes(status)) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap ${style?.text || "text-zinc-500"}`}
        title={
          status === "cancelled"
            ? "Cancelled — the work is not happening, so there is nothing to measure."
            : `Closed${completedAt ? ` ${fmtDay(completedAt)}` : ""} — finished with administratively.`
        }
      >
        {style?.dot && (
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: style.dot }} />
        )}
        {style?.label || status}
      </span>
    );
  }

  /* ---------- live: the status, then how the delivery landed ---------- */
  if (status === "live") {
    const early = daysEarly(completedAt, revised, eta);

    if (early === null) {
      // Delivered, but one of the two dates is missing, so there is no figure
      // to state. Says so plainly rather than showing a stale running total.
      return (
        <span
          className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 whitespace-nowrap"
          title={
            completedAt
              ? `Live ${fmtDay(completedAt)} — no ETA recorded to compare against.`
              : "Live — no completion date recorded, so there is nothing to compare with the ETA."
          }
        >
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: STATUS_STYLES.live.dot }} />
          Live
        </span>
      );
    }

    const overran = early < 0;
    return (
      <span
        className={`text-xs whitespace-nowrap ${overran ? "text-amber-700" : "text-emerald-700"}`}
        title={
          overran
            ? `Live ${fmtDay(completedAt)} — took ${-early} ${plural(early)} longer than the ${against}${on ? ` of ${on}` : ""}`
            : early === 0
              ? `Live ${fmtDay(completedAt)} — delivered on the ${against}${on ? ` of ${on}` : ""}`
              : `Live ${fmtDay(completedAt)} — delivered ${early} ${plural(early)} before the ${against}${on ? ` of ${on}` : ""}`
        }
      >
        Live <span className="font-mono tabular-nums font-semibold">({signed(early)})</span>
      </span>
    );
  }

  /* ---------- in flight: the running clock ---------- */
  const left = daysLeft(revised, eta);

  if (left === null) {
    return (
      <span
        className="font-mono text-xs text-zinc-400"
        title="No ETA or revised ETA recorded, so there is nothing to measure against."
      >
        —
      </span>
    );
  }

  const over = left < 0;
  const title = over
    ? `${-left} ${plural(left)} past the ${against}${on ? ` of ${on}` : ""}`
    : left === 0
      ? `Due today — the ${against}${on ? ` is ${on}` : ""}`
      : `${left} ${plural(left)} until the ${against}${on ? ` of ${on}` : ""}`;

  // Over is the signal, so it is the only state that gets weight and colour.
  // Everything else stays quiet: a grid where most rows shout has no signal
  // left.
  const tone = over
    ? "text-red-700 font-semibold"
    : left === 0
      ? "text-amber-700 font-semibold"
      : "text-zinc-600";

  return (
    <span className={`font-mono text-xs tabular-nums whitespace-nowrap ${tone}`} title={title}>
      {signed(left)}
      {/* A dot marks the rows whose figure is only that healthy because the
          date was moved — the same marker the Revised ETA column uses. */}
      {revised && <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-zinc-400 align-middle" />}
    </span>
  );
}
